import * as React from "react"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { MessageContent } from "./message"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import {
  AlertCircle,
  Download,
  ExternalLink,
  Eye,
  File as FileIcon,
  FileAudio,
  FileImage,
  FileText,
  FileVideo,
  Loader2,
} from "lucide-react"
import useDownloadFileName from "@/hooks/useDownloadFileName"
import useHandleDownload from "@/hooks/useHandleDownload"
import useMessage from "@/components/ui/message/useMessage"

export type BodyType =
  | "image"
  | "video"
  | "audio"
  | "pdf"
  | "document"
  | "archive"
  | "code"
  | "unknown"

export interface MessageFileProps extends React.HTMLAttributes<HTMLDivElement> {
  fileSize?: number
  fileType?: BodyType
  fileUrl?: string
  thumbnailUrl?: string
  downloadUrl?: string
  mimeType?: string
  uploadProgress?: number
  isUploading?: boolean
  uploadError?: string
  onDownload?: () => void
  onPreview?: () => void
  onExternalView?: () => void
  showPreview?: boolean
  showDownload?: boolean
  showSize?: boolean
  forwarded?: boolean
  reply?: boolean
}

const MessageFile = React.forwardRef<HTMLDivElement, MessageFileProps>(
  (
    {
      className,
      fileSize,
      fileType = "unknown",
      fileUrl,
      thumbnailUrl,
      downloadUrl,
      mimeType,
      uploadProgress,
      isUploading = false,
      uploadError,
      onDownload,
      onPreview,
      onExternalView,
      showPreview = true,
      showDownload = true,
      showSize = true,
      forwarded = false,
      reply = false,
      children,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const message = useMessage({ forwarded, reply })
    const fileName = message.fileName || ""

    const downloadFileName = useDownloadFileName(message)
    const download = useHandleDownload()

    const handleDownload = onDownload
      ? onDownload
      : downloadFileName && fileUrl
        ? (e: React.MouseEvent) => {
            e.stopPropagation()
            download(fileUrl, downloadFileName)
          }
        : undefined

    const getFileIcon = (type: BodyType) => {
      switch (type) {
        case "image":
          return <FileImage className="h-5 w-5" />
        case "video":
          return <FileVideo className="h-5 w-5" />
        case "audio":
          return <FileAudio className="h-5 w-5" />
        case "pdf":
        case "document":
          return <FileText className="h-5 w-5" />
        default:
          return <FileIcon className="h-5 w-5" />
      }
    }

    const getFileTypeColor = (type: BodyType) => {
      switch (type) {
        case "image":
          return "text-green-600 bg-green-50 dark:bg-green-900/20"
        case "video":
          return "text-purple-600 bg-purple-50 dark:bg-purple-900/20"
        case "audio":
          return "text-blue-600 bg-blue-50 dark:bg-blue-900/20"
        case "pdf":
          return "text-red-600 bg-red-50 dark:bg-red-900/20"
        case "document":
          return "text-blue-600 bg-blue-50 dark:bg-blue-900/20"
        case "archive":
          return "text-orange-600 bg-orange-50 dark:bg-orange-900/20"
        case "code":
          return "text-gray-600 bg-gray-50 dark:bg-gray-900/20"
        default:
          return "text-gray-600 bg-gray-50 dark:bg-gray-900/20"
      }
    }

    const formatFileSize = (bytes?: number) => {
      if (!bytes) return ""

      const units = ["B", "KB", "MB", "GB"]
      let size = bytes
      let unitIndex = 0

      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024
        unitIndex++
      }

      return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`
    }

    const getFileExtension = (fileName: string) => {
      const extension = fileName.split(".").pop()?.toUpperCase()
      return extension || t("chat.file.extensionFallback")
    }

    const handlePreview = () => {
      if (onPreview) {
        onPreview()
      } else if (fileUrl) {
        window.open(fileUrl, "_blank")
      }
    }

    const handleExternalView = () => {
      if (onExternalView) {
        onExternalView()
      } else if (fileUrl) {
        window.open(fileUrl, "_blank")
      }
    }

    return (
      <MessageContent ref={ref} {...props}>
        <div
          className={cn(
            "flex max-w-sm items-center gap-3 rounded-lg border bg-card p-3",
            uploadError && "border-destructive bg-destructive/5",
            className,
          )}
        >
          {/* File Icon/Thumbnail */}
          <div className="flex-shrink-0">
            {thumbnailUrl ? (
              <div className="relative h-12 w-12 overflow-hidden rounded bg-muted">
                <img
                  src={thumbnailUrl}
                  alt={fileName}
                  className="h-full w-full object-cover"
                />
              </div>
            ) : (
              <div
                className={cn(
                  "flex h-12 w-12 items-center justify-center rounded",
                  getFileTypeColor(fileType),
                )}
              >
                {isUploading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : uploadError ? (
                  <AlertCircle className="h-5 w-5 text-destructive" />
                ) : (
                  getFileIcon(fileType)
                )}
              </div>
            )}
          </div>

          {/* File Info */}
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2">
              <h4 className="truncate text-sm font-medium" title={fileName}>
                {fileName}
              </h4>
              <span
                className={cn(
                  "rounded px-1.5 py-0.5 font-mono text-xs",
                  getFileTypeColor(fileType),
                )}
              >
                {getFileExtension(fileName)}
              </span>
            </div>

            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {showSize && fileSize && <span>{formatFileSize(fileSize)}</span>}
              {mimeType && <span>{mimeType}</span>}
            </div>

            {/* Upload Progress */}
            {isUploading && typeof uploadProgress === "number" && (
              <div className="mt-2">
                <Progress value={uploadProgress} className="h-1" />
                <div className="mt-1 text-xs text-muted-foreground">
                  {t("chat.file.uploading", { progress: uploadProgress })}
                </div>
              </div>
            )}

            {/* Upload Error */}
            {uploadError && (
              <div className="mt-1 text-xs text-destructive">{uploadError}</div>
            )}
          </div>

          {/* Action Buttons */}
          {!isUploading && !uploadError && (
            <div className="flex flex-col gap-1">
              {showPreview && (fileType === "image" || fileType === "pdf") && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handlePreview}
                  title={t("chat.file.preview")}
                >
                  <Eye className="h-3 w-3" />
                </Button>
              )}

              {showDownload && (downloadUrl || fileUrl) && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handleDownload}
                  title={t("chat.media.download")}
                >
                  <Download className="h-3 w-3" />
                </Button>
              )}

              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={handleExternalView}
                title={t("chat.media.openExternally")}
              >
                <ExternalLink className="h-3 w-3" />
              </Button>
            </div>
          )}

          {/* Retry button for failed uploads */}
          {uploadError && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={() => {
                // This would typically trigger a retry mechanism
                console.log("Retry upload")
              }}
            >
              {t("common.retry")}
            </Button>
          )}
        </div>

        {children}
      </MessageContent>
    )
  },
)

MessageFile.displayName = "MessageFile"

export const detectType = (fileName?: string): BodyType | undefined => {
  if (!fileName) return undefined
  const extension = fileName.split(".").pop()?.toLowerCase()

  if (extension) {
    const imageExts = ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"]
    const videoExts = ["mp4", "avi", "mov", "wmv", "flv", "webm", "mkv"]
    const audioExts = ["mp3", "wav", "ogg", "aac", "flac", "m4a"]
    const documentExts = ["doc", "docx", "txt", "rtf", "odt"]
    const archiveExts = ["zip", "rar", "7z", "tar", "gz"]
    const codeExts = ["js", "ts", "html", "css", "py", "java", "cpp", "c"]

    if (imageExts.includes(extension)) return "image"
    if (videoExts.includes(extension)) return "video"
    if (audioExts.includes(extension)) return "audio"
    if (extension === "pdf") return "pdf"
    if (documentExts.includes(extension)) return "document"
    if (archiveExts.includes(extension)) return "archive"
    if (codeExts.includes(extension)) return "code"
  }

  return undefined
}

export { MessageFile }
